<script lang="ts">
	import CityFeaturesStructure from '$lib/components/city/features/CityFeaturesStructure.svelte';
	import SpotlightItem from '$lib/components/city/features/SpotlightItem.svelte';
	import DiscoveryCard from '$lib/components/ui/DiscoveryCard.svelte';
	import HeroNeutral from '$lib/components/ui/HeroNeutral.svelte';
	import { Importance } from '$lib/features/_other/importance.enum';
	import type { PlaceLocation } from '$lib/features/_other/placeLocation';
	import {
		CITY_FEATURE_TYPE_COLOURS,
		CITY_FEATURE_TYPE_ICON_MAP,
		CityFeatureType
	} from '$lib/features/cityFeature/cityFeatureType.enum';
	import type { GreenSpacePreview } from '$lib/features/greenSpace/greenSpace.type';
	import type { CoverImage } from '$lib/features/image/imageDetails.type';
	import { FULL_SITE_URL, MAX_SPOTLIGHT_ITEMS } from '$lib/statics';
	import { findFirstAvailableImage, listFormatByAnd, sortShortestToLongest } from '$lib/utils/general.util';
	import { serializeJsonLd } from '$lib/utils/schema.util';
	import { UrlUtil } from '$lib/utils/url.util';
	import type { Output } from './+page.server';

	const featureType: CityFeatureType = CityFeatureType.GREEN_SPACE;

	const { data }: { data: { output: Output } } = $props();

	const output = $derived<Output>(data.output);
	const location = $derived<PlaceLocation>(output.location);
	const spotlightItems = $derived.by<GreenSpacePreview[]>(() => {
		const importantItems = output.items.filter((a) => a.feature.importance === Importance.ESSENTIAL);
		const itemsToFeature = importantItems.length > 0 ? importantItems : output.items;
		return itemsToFeature.slice(0, MAX_SPOTLIGHT_ITEMS);
	});
	const otherItems = $derived<GreenSpacePreview[]>(output.items.filter((a) => !spotlightItems.includes(a)));
	const meta = $derived.by(() => {
		const orderedNames: string[] = sortShortestToLongest(
			[...spotlightItems, ...otherItems].map((item) => item.feature.englishName)
		);
		const shortestName: string = orderedNames[0];
		const firstAvailableImage: CoverImage | undefined = findFirstAvailableImage(output.items);
		const itemsLength = output.items.length;
		const description =
			itemsLength === 1
				? `Discover ${location.city.label}'s top nature spot: ${shortestName}, perfect for a relaxing day outdoors.`
				: itemsLength === 2
					? `Discover ${location.city.label}'s top ${itemsLength} nature and parks: ${listFormatByAnd(orderedNames)}, both perfect for a relaxing day outdoors.`
					: `Explore ${itemsLength} must-see nature and parks for a calm day outdoors in ${location.city.label}, including ${listFormatByAnd(orderedNames.slice(0, 4))}.`;
		return {
			title: `${location.city.label} Nature and Parks: ${shortestName} & More`,
			description: description,
			canonicalUrl: `${FULL_SITE_URL}/${UrlUtil.toCityFeatures({ location, type: featureType }).href}`,
			imageUrl: firstAvailableImage ? UrlUtil.getFullImageUrl(firstAvailableImage) : undefined
		};
	});
	const jsonLdTouristAttractionsCollection = $derived.by(() => {
		const json = data.output.jsonLdTouristAttractionsCollection;
		json.name = meta.title;
		json.description = meta.description;
		return json;
	});
	const combinedJsonLd = $derived([data.output.jsonLdBreadcrumbs, jsonLdTouristAttractionsCollection]);
</script>

<svelte:head>
	<title>{meta.title}</title>
	<meta name="description" content={meta.description} />
	<link rel="canonical" href={meta.canonicalUrl} />

	<meta property="og:title" content={meta.title} />
	<meta property="og:description" content={meta.description} />
	<meta property="og:url" content={meta.canonicalUrl} />
	<meta property="og:image" content={meta.imageUrl} />
	<meta property="og:type" content="website" />

	<meta property="twitter:title" content={meta.title} />
	<meta property="twitter:description" content={meta.description} />
	<meta property="twitter:url" content={meta.canonicalUrl} />
	<meta property="twitter:image" content={meta.imageUrl} />
	<meta property="twitter:card" content="summary_large_image" />

	{@html `<script type="application/ld+json">${serializeJsonLd(combinedJsonLd)}</script>`}
</svelte:head>

<CityFeaturesStructure {location} {spotlightItems} {otherItems} keepExploring={output.keepExploring}>
	{#snippet HeaderSnippet()}
		<HeroNeutral
			title={`Best Nature and Parks in ${location.city.label}`}
			description={`Need a break from the bustle? Discover ${output.items.length} tranquil parks, gardens, and nature reserves. From vast urban forests to hidden botanical corners, here is where ${location.city.label} goes to breathe.`}
			breadcrumbs={data.output.breadcrumbs}
			decorations={{
				hexColour: CITY_FEATURE_TYPE_COLOURS[featureType]
			}}
		/>
	{/snippet}

	{#snippet SpotlightItemSnippet(item: GreenSpacePreview, index: number)}
		<SpotlightItem
			elementId={item.feature.slug}
			feature={item.feature}
			language={data.output.countryLanguage}
			nav={UrlUtil.toCityFeature({ location, type: item.feature.type, featureSlug: item.feature.slug })}
			fact={item.fact}
			isReversed={index % 2 !== 0}
			lazyLoadImage={index >= 2}
		/>
	{/snippet}

	{#snippet OtherItemSnippet(item: GreenSpacePreview)}
		<DiscoveryCard
			headingTag="h3"
			elementId={item.feature.slug}
			englishName={item.feature.englishName}
			nativeName={item.feature.nativeName}
			description={item.feature.description}
			language={data.output.countryLanguage}
			images={item.feature.images}
			nav={UrlUtil.toCityFeature({ location, type: item.feature.type, featureSlug: item.feature.slug })}
			NoImagePlaceholderIcon={CITY_FEATURE_TYPE_ICON_MAP[featureType]}
		/>
	{/snippet}
</CityFeaturesStructure>
